import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner, PrismIcon } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import * as Highcharts from 'highcharts';
import HotelBenchmarkInsightTooltipsSharedTooltipTable from 'lumen/components/hotel/benchmark-insight/tooltips/shared/tooltip-table';
import HotelBenchmarkInsightTooltipsSharedTooltipTitle from 'lumen/components/hotel/benchmark-insight/tooltips/shared/tooltip-title';
import type { BeniBreakdown, BeniMetaComparisonSet } from 'lumen/models';
import type {
  Kpi,
  ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';

// Pass either of these 2 combinations:
// - prefix
// - date and dateAggregation
// but never both combinations at the same time.
type PrefixOrDateProps =
  | {
      date?: never;
      dateAggregation?: never;
      prefix: string;
    }
  | {
      date: Dayjs;
      dateAggregation: string;
      prefix?: never;
    };

export type BeniTooltipData = {
  ownComparisonSetBreakdown: BeniBreakdown | null;
  primaryComparisonSetBreakdown: BeniBreakdown | null;
  secondaryComparisonSetBreakdown: BeniBreakdown | null;
  showWarningForWeek?: boolean;
} & PrefixOrDateProps;

interface HotelBenchmarkInsightTooltipsBeniTooltipSignature {
  Args: {
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    pickupTranslation?: string;
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    referencePeriod: ReferencePeriod;
    secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
    selectedKpi: Kpi;
    // Don't pass pickupTranslation if we don't want to show pickup
    selectedRange: DateRangeType;
    showMix: boolean;
    tooltipData: BeniTooltipData;
    varianceTranslation?: string;
    visibleKpis: Kpi[];
  };
  Blocks: {
    default: [];
  };
}

export default class HotelBenchmarkInsightTooltipsBeniTooltip extends Component<HotelBenchmarkInsightTooltipsBeniTooltipSignature> {
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  get isPast(): boolean {
    const { end } = this.args.selectedRange;

    return this.benchmarkInsightBeniService.isPastRange(end);
  }

  // We enforce with TS that either prefix or date is passed, but not both.
  // In this getter, we either use prefix or date, depending on which one was passed.
  get prefixOrFormattedDate(): string {
    const { tooltipData } = this.args;
    const { prefix } = tooltipData;

    if (prefix) {
      return prefix;
    }

    const { date, dateAggregation } = tooltipData;

    // TS doesn't know that date must have been passed at this point, because it's enforced in the signature. So we cast it.
    const dateWillExist = date as Dayjs;

    if (dateAggregation === DATE_AGGREGATION.WEEK) {
      const dateFormatted = Highcharts.dateFormat(
        this.dates.highchartsLabelFormats[dateAggregation],
        this.hotelAndMarketService.toLocalUnixTime(dateWillExist.unix()),
      );

      return this.intl.t('general.week_of', {
        date: dateFormatted,
      });
    }

    return dateWillExist.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_SHORT,
    );
  }

  get showBanner(): boolean {
    return !this.args.ownComparisonSetMeta?.isVisible;
  }

  get showPickup(): boolean {
    return Boolean(this.args.pickupTranslation);
  }

  <template>
    <div
      class="tooltip-benchmark-insight default-tooltip default-tooltip--no-max-width default-table-tooltip"
    >
      <HotelBenchmarkInsightTooltipsSharedTooltipTitle
        @isPast={{this.isPast}}
        @pickupTranslation={{@pickupTranslation}}
        @prefix={{this.prefixOrFormattedDate}}
        @primaryComparisonSetMeta={{@primaryComparisonSetMeta}}
        @selectedKpi={{@selectedKpi}}
        @showMix={{@showMix}}
        @varianceTranslation={{@varianceTranslation}}
      />

      {{#if this.showBanner}}
        <div class="mt-200 mr-400 mb-200 ml-400">
          <PrismBanner
            @bannerMessage={{t
              "benchmark_insight.trial_without_data.chart_tooltip_banner"
            }}
            @bannerStyle="info"
            @icon="circle-information-outline"
          />
        </div>
      {{/if}}

      {{yield}}

      <HotelBenchmarkInsightTooltipsSharedTooltipTable
        @isPast={{this.isPast}}
        @ownComparisonSetBreakdown={{@tooltipData.ownComparisonSetBreakdown}}
        @ownComparisonSetMeta={{@ownComparisonSetMeta}}
        @primaryComparisonSetBreakdown={{@tooltipData.primaryComparisonSetBreakdown}}
        @primaryComparisonSetMeta={{@primaryComparisonSetMeta}}
        @referencePeriod={{@referencePeriod}}
        @secondaryComparisonSetBreakdown={{@tooltipData.secondaryComparisonSetBreakdown}}
        @secondaryComparisonSetMeta={{@secondaryComparisonSetMeta}}
        @selectedKpi={{@selectedKpi}}
        @showMix={{@showMix}}
        @showPickup={{this.showPickup}}
        @visibleKpis={{@visibleKpis}}
      />

      {{#if @tooltipData.showWarningForWeek}}
        <div
          class="incomplete-period-row tooltip-benchmark-insight__incomplete-period-row"
        >
          <span class="mr-100 ml-100"><PrismIcon
              @color="warning"
              @name="warning"
              @size="small"
            /></span>
          <span class="flex-item mr-100">
            {{t "general.incomplete_period_detail.week"}}
          </span>

          <span
            class="flex-item tooltip-benchmark-insight__incomplete-period-row__extra
              {{if
                (or
                  (eq this.hotelAndMarketService.user.locale 'de')
                  (eq this.hotelAndMarketService.user.locale 'es')
                )
                'indentation'
              }}"
          >
            {{t "general.incomplete_period_extra"}}
          </span>
        </div>
      {{/if}}
    </div>
  </template>
}
